import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useGql } from '@ah/design';
import { Header, PillTabs, PullToRefresh, type PillTab } from '@ah/design/mobile';
import { Podcast } from 'lucide-react';
import { PODCAST_SERIES_LIST_QUERY, type SeriesListItem } from '../shared/index.ts';
import { ShowsGridSkeleton, ErrorRetry } from './_parts.tsx';

const TABS: PillTab[] = [
  { to: '/podcasts',        label: 'For You', end: true },
  { to: '/podcasts/latest', label: 'Latest' },
  { to: '/podcasts/shows',  label: 'Shows' },
  { to: '/podcasts/seen',   label: 'Heard' },
  { to: '/podcasts/saved',  label: 'Saved' },
];

export function PodcastShows() {
  const gql = useGql();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['podcasts', 'series', 'list'],
    queryFn: async () => {
      const res = await gql.request<{ podcastSeriesList: SeriesListItem[] }>(PODCAST_SERIES_LIST_QUERY);
      return res.podcastSeriesList;
    },
    refetchInterval: 60_000,
  });

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title="Podcasts" />
      <PillTabs tabs={TABS} />
      <PullToRefresh className="flex-1 px-3 py-3" onRefresh={() => refetch()}>
        {isLoading ? (
          <ShowsGridSkeleton />
        ) : error ? (
          <ErrorRetry label="Could not load shows." onRetry={() => refetch()} />
        ) : !data || data.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Podcast size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No watchlisted shows yet — the crawler is warming up.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {data.map((s) => <SeriesCell key={s.id} s={s} />)}
          </div>
        )}
      </PullToRefresh>
    </div>
  );
}

function SeriesCell({ s }: { s: SeriesListItem }) {
  return (
    <Link to={`/podcasts/series/${s.id}`} className="block space-y-2">
      <div className="aspect-square rounded-xl overflow-hidden bg-muted">
        {s.imageUrl ? (
          <img src={s.imageUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full grid place-items-center text-muted-foreground opacity-40">
            <Podcast size={28} />
          </div>
        )}
      </div>
      <p className="text-sm font-medium leading-snug line-clamp-1">{s.name}</p>
    </Link>
  );
}
